export type CrossBrandComponentId = 'lipstick' | 'blush' | 'eyeshadow';

// Perceptual match quality, derived from the CIEDE2000 ΔE between the look
// colour and the candidate shade. Tiers are emitted by the matcher
// (scripts/match_cross_brand_looks.mjs); matchQuality() re-derives them at
// runtime as a fallback for older data files without the field.
export type MatchQuality = 'perfect' | 'excellent' | 'good' | 'approximate';

export interface CrossBrandMatchItem {
  brand: string;
  category: string;
  hex: string;
  shade_name: string;
  product_name: string;
  product_line: string | null;
  finish: string | null;
  price: number | null;
  currency: string | null;
  image_url: string | null;
  product_url: string | null;
  sku: string | null;
  delta_e: number;
  match_quality?: MatchQuality;
  // Retailer the shade is sourced from (e.g. 'sephora_it'); absent for
  // brand-direct catalogs. Carried for affiliate / buy-link routing.
  retailer?: string;
}

// Keep in sync with QUALITY_TIERS in scripts/match_cross_brand_looks.mjs.
export function matchQuality(item: CrossBrandMatchItem): MatchQuality {
  if (item.match_quality) return item.match_quality;
  if (item.delta_e <= 2) return 'perfect';
  if (item.delta_e <= 3.5) return 'excellent';
  if (item.delta_e <= 5) return 'good';
  return 'approximate';
}

export interface CrossBrandComponent {
  id: CrossBrandComponentId;
  category: string;
}

export interface CrossBrandLookMatch {
  lipstick?: CrossBrandMatchItem[];
  blush?: CrossBrandMatchItem[];
  eyeshadow?: CrossBrandMatchItem[];
}

export interface CrossBrandMatchesFile {
  _generated_at: string;
  _sources: string[];
  _limit_per_component: number;
  _delta_e_metric?: string;
  _max_delta_e?: number;
  _quality_tiers?: { tier: MatchQuality; maxDeltaE: number }[];
  counts: {
    looks: number;
    candidates: number;
    matched_items: number;
    slots?: number;
    empty_slots?: number;
  };
  empty_slots?: string[];
  components: CrossBrandComponent[];
  matches: Record<string, CrossBrandLookMatch>;
}

export const BRAND_LABELS: Record<string, string> = {
  loreal: "L'Oréal Paris",
  mac: 'MAC',
  kiko: 'Kiko Milano',
  nyx: 'NYX Professional Makeup',
  maybelline: 'Maybelline New York',
  essence: 'essence',
  catrice: 'Catrice',
  revolution: 'Makeup Revolution',
  elf: 'e.l.f. Cosmetics',
  wnw: 'Wet n Wild',
  // Sephora IT prestige brands whose canonical casing differs from a plain
  // word-by-word title-case of the slug.
  nars: 'NARS',
  ysl: 'YSL',
  'yves-saint-laurent': 'Yves Saint Laurent',
  'sephora-collection': 'Sephora Collection',
  'huda-beauty': 'Huda Beauty',
  'haus-labs-by-lady-gaga': 'Haus Labs',
  'make-up-for-ever': 'Make Up For Ever',
  'anastasia-beverly-hills': 'Anastasia Beverly Hills'
};

export function brandLabel(brand: string): string {
  if (BRAND_LABELS[brand]) return BRAND_LABELS[brand];
  // Fallback: title-case each hyphen-separated word ("charlotte-tilbury" →
  // "Charlotte Tilbury", "fenty-beauty" → "Fenty Beauty").
  return brand
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

let cached: CrossBrandMatchesFile | null = null;

export async function loadCrossBrandLookMatches(): Promise<CrossBrandMatchesFile | null> {
  if (cached) return cached;
  try {
    const response = await fetch('/data/vto-cross-brand-matches.json');
    if (!response.ok) return null;
    cached = (await response.json()) as CrossBrandMatchesFile;
    return cached;
  } catch {
    return null;
  }
}
